import * as React from 'react'
import { StyleSheet } from 'react-native'
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native'
import Clipboard from 'expo-clipboard'
import crypto from 'crypto'
import {
  PlanetProposal,
  PlanetSymbolDoc,
  planetPath,
  planetSymbolPath,
  planetsCollectionPath,
} from '@mv/api/lib/src/schema/planets'
import {
  convertEthereumAddressToDisplayAddress,
  convertDisplayAddressToEthereumAddress,
  addressDisplayPrefixReplacement,
} from '@mv/api/lib/src/schema/accounts'
import { logger } from '../../../../logger'
import { useSelector } from '../../../../state/store'
import { View, Text, Section } from '../../..'
import { Button } from '../../../Button'
import { Heading } from '../../../Screen'
import {
  PlanetsNavigationProps,
  PlanetsStackParamList,
} from '../../../../navigation/types'
import {
  db,
  doc,
  getDoc,
  DocumentReference,
  query,
  collection,
  CollectionReference,
  where,
  limit,
  getDocs,
} from '../../../../firebase/firestore'
import { Loading } from '../../../Loading'
import { RenderPlanet, tempPlanetColorFromHexString } from '../../RenderPlanet'
import { TabView } from '../../../TabView'
import { PLANET_NOT_FOUND } from '../../../../constants/messages'
import { AnnouncementsTabDisplay } from './AnnouncementsTabDisplay'
import { useTheme } from '../../../../hooks/useTheme'

export function OverviewTab(): JSX.Element {
  const { theme } = useTheme()
  const [notFound, setNotFound] = React.useState(false)
  const [planet, setPlanet] = React.useState<PlanetProposal | undefined>(
    undefined
  )

  const navigation = useNavigation<PlanetsNavigationProps>()
  const route = useRoute<RouteProp<PlanetsStackParamList, 'PlanetsShow'>>()
  const planetLocator = route.params?.planetLocator || 'AI'

  const user = useSelector((state) => state.user)

  React.useEffect(() => {
    async function getPlanetFromPlanetLocator() {
      if (
        !(
          planetLocator.startsWith('0x') ||
          planetLocator.startsWith(addressDisplayPrefixReplacement)
        )
      ) {
        // planetLocator is likely a symbol
        const symbolDocSnapshot = await getDoc(
          doc(
            db,
            planetSymbolPath(planetLocator.trim().toUpperCase())
          ) as DocumentReference<PlanetSymbolDoc>
        )
        if (symbolDocSnapshot.exists()) {
          const symbolDoc = symbolDocSnapshot.data()
          const planetDocSnapshot = await getDoc(
            doc(
              db,
              planetPath(symbolDoc.planet)
            ) as DocumentReference<PlanetProposal>
          )
          if (planetDocSnapshot.exists()) {
            setPlanet(planetDocSnapshot.data())
            return
          }
        }
      }
      if (
        RegExp(`^(0x|${addressDisplayPrefixReplacement})[a-fA-F0-9]{40}$`).test(
          planetLocator
        )
      ) {
        // planetLocator appears to be a complete planetId
        const planetDocSnapshot = await getDoc(
          doc(
            db,
            planetPath(convertDisplayAddressToEthereumAddress(planetLocator))
          ) as DocumentReference<PlanetProposal>
        )
        if (planetDocSnapshot.exists()) {
          setPlanet(planetDocSnapshot.data())
          return
        }
      }
      if (
        planetLocator.startsWith('0x') ||
        planetLocator.startsWith(addressDisplayPrefixReplacement)
      ) {
        const normPlanetLocator =
          convertDisplayAddressToEthereumAddress(planetLocator).toLowerCase()
        // planetLocator is not an exact match for the planetId - try for a loose match
        const planetCollectionDocsSnapshot = await getDocs(
          query(
            collection(
              db,
              planetsCollectionPath
            ) as CollectionReference<PlanetProposal>,
            where('lowercaseId', '>=', normPlanetLocator),
            limit(2)
          )
        )
        if (!planetCollectionDocsSnapshot.empty) {
          const potentialPlanetDocs = planetCollectionDocsSnapshot.docs.map(
            (snapshot) => snapshot.data()
          )
          if (
            potentialPlanetDocs[0]?.lowercaseId.startsWith(normPlanetLocator) &&
            (potentialPlanetDocs.length === 1 ||
              !potentialPlanetDocs[1]?.lowercaseId.startsWith(
                normPlanetLocator
              ))
          ) {
            // one match, this is the planet we are looking for
            setPlanet(potentialPlanetDocs[0])
            return
          }
        }
      }
      setNotFound(true)
    }
    getPlanetFromPlanetLocator().catch((e) =>
      logger.warn(
        'error encountered when getting planet from planetLocator: ',
        e
      )
    )
  }, [planetLocator])

  React.useEffect(() => {
    if (planet && planetLocator !== planet.symbol) {
      navigation.replace('PlanetsShow', { planetLocator: planet.symbol })
    }
  }, [planet, planetLocator, navigation])

  if (notFound) {
    return (
      <TabView>
        <Heading>Multiverse Planets</Heading>
        <Text>{PLANET_NOT_FOUND}</Text>
      </TabView>
    )
  }

  if (planet) {
    if (planet.symbol === 'AI') {
      return <AnnouncementsTabDisplay planet={planet} />
    }
    const planetDisplayAddress = convertEthereumAddressToDisplayAddress(
      planet.id
    )
    // const { founderAddress } = planet
    const founderAddress = `0x${crypto
      .createHash('sha1')
      .update(planet.id)
      .digest('hex')}`
    return (
      <TabView>
        {theme === 'hadron' && (
          <RenderPlanet
            style={styles.planetRender}
            color={
              planet._.details.renderOptions?.color ||
              tempPlanetColorFromHexString(planet.id)
            }
            textureMapUrl={planet._.details.renderOptions?.textureMapUrl}
            showWireframe={planet._.details.renderOptions?.showWireframe}
            useTextureMapAsBumpMap={
              planet._.details.renderOptions?.useTextureMapAsBumpMap
            }
            size={{ width: 200, height: 200 }}
          />
        )}
        <Section heading="Symbol">
          <Text preset="paragraph">{planet.symbol}</Text>
        </Section>

        <Section heading="Planetary Stage">
          <Text preset="paragraph">
            {planet._.details.displayOverrides?.stage || 'Space Dust'}
          </Text>
        </Section>

        <Section heading="Founder">
          <Text preset="paragraph">
            {(!!planet._.details.displayOverrides?.founderName &&
              `${
                planet._.details.displayOverrides?.founderName
              } (${convertEthereumAddressToDisplayAddress(founderAddress)})`) ||
              convertEthereumAddressToDisplayAddress(founderAddress)}
          </Text>
        </Section>

        <Section heading="Planet Address">
          <View style={styles.planetAddress}>
            <Button
              onPress={async () => {
                if (user.user?.account?.address)
                  Clipboard.setString(planetDisplayAddress)
              }}
            >
              <Text preset="paragraph">{planetDisplayAddress} ⧉</Text>
            </Button>
          </View>
        </Section>

        <Section heading="Description">
          <Text preset="paragraph">
            {planet._.details.description || 'No description yet'}
          </Text>
        </Section>
        {!!planet._.details.benefits && (
          <Section heading="Staker Benefits">
            <Text preset="paragraph">{planet._.details.benefits}</Text>
          </Section>
        )}
        {!!planet._.details.accountability && (
          <Section heading="Accountability">
            <Text preset="paragraph">{planet._.details.accountability}</Text>
          </Section>
        )}
      </TabView>
    )
  }
  return <Loading />
}

const styles = StyleSheet.create({
  planetAddress: {
    flexDirection: 'row',
  },
  planetRender: {
    position: 'absolute',
    right: 0,
  },
})
